
import React, { useState } from "react";
import { BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from "recharts";

const vehicleUsageData = [
  { category: "Motorcycles", fuelConsumption: 3.5 },
  { category: "Passenger Cars", fuelConsumption: 7.2 },
  { category: "Commercial Vehicles", fuelConsumption: 12.1 },
  { category: "Fleet", fuelConsumption: 9.5 }
];

// Filter data to show only high traffic times
const timeOfDayData = [
  { time: "6-9am", traffic: 85 },
  { time: "9am-12pm", traffic: 45 },
  { time: "12-3pm", traffic: 50 },
  { time: "3-6pm", traffic: 90 },
  { time: "6-9pm", traffic: 65 },
  { time: "9pm-12am", traffic: 30 },
  { time: "12-6am", traffic: 15 }
];

// Filter to only high traffic times (traffic >= 65)
const highTrafficData = timeOfDayData.filter(item => item.traffic >= 65);

const customerOriginData = [
  { name: "Within 2km", value: 45 },
  { name: "2-5km", value: 30 },
  { name: "5-10km", value: 15 },
  { name: "10km+", value: 10 }
];

const COLORS = ["#8B5CF6", "#A78BFA", "#C4B5FD", "#DDD6FE"];

const CustomerFlow = ({ station }) => {
  // Fixed at high traffic (100)
  const [timeRange, setTimeRange] = useState([0, 100]);
  const [selectedTab, setSelectedTab] = useState("overview");

  // This function is now just for visual effect - doesn't actually change data
  const handleTimeRangeChange = (e) => {
    // The value will reset back to 100 after user interaction
    setTimeout(() => {
      setTimeRange([0, 100]);
    }, 500);
  };

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Traffic Flow by Time of Day</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={highTrafficData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="traffic" fill="#8B5CF6" />
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Traffic Intensity
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={timeRange[1]}
              onChange={handleTimeRangeChange}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer opacity-70"
              style={{ pointerEvents: "none" }}
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Low Traffic</span>
              <span className="font-bold text-purple-700">High Traffic</span>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Customer Origin Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={customerOriginData}
                cx="50%"
                cy="50%"
                labelLine={true}
                outerRadius={100}
                fill="#8B5CF6"
                dataKey="value"
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
              >
                {customerOriginData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => `${value}%`} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow mb-8">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Vehicle Usage & Fuel Consumption</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={vehicleUsageData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="category" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="fuelConsumption" fill="#8B5CF6" name="Avg. Fuel Consumption (L/day)" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Customer Flow Actions</h3>
        <div className="bg-purple-50 p-4 rounded-md mb-6">
          <h4 className="font-medium text-purple-800 mb-2">AI Insights</h4>
          <p className="text-sm text-gray-700">
            This station experiences high traffic during morning (6-9am) and evening (3-6pm) rush hours, with 45% of customers coming from within 2km.
            Consider targeted promotions during peak times and expanding your reach to customers in the 5-10km radius.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border border-gray-200 rounded-md p-4">
            <h4 className="font-medium text-gray-900 mb-2">Recommended Actions</h4>
            <ul className="text-sm space-y-2">
              <li className="flex items-start">
                <span className="inline-block w-4 h-4 bg-purple-100 rounded-full mr-2 mt-1"></span>
                <span>Deploy additional staff during 6-9am and 3-6pm peak periods</span>
              </li>
              <li className="flex items-start">
                <span className="inline-block w-4 h-4 bg-purple-100 rounded-full mr-2 mt-1"></span>
                <span>Target motorcycle riders with specialized promotions</span>
              </li>
              <li className="flex items-start">
                <span className="inline-block w-4 h-4 bg-purple-100 rounded-full mr-2 mt-1"></span>
                <span>Implement signage visible from major roads within 5km radius</span>
              </li>
            </ul>
          </div>
          
          <div className="border border-gray-200 rounded-md p-4">
            <h4 className="font-medium text-gray-900 mb-2">Impact Forecast</h4>
            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Volume Increase</span>
                  <span className="font-medium">+8.5%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-purple-600 h-2 rounded-full" style={{ width: "8.5%" }}></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Customer Retention</span>
                  <span className="font-medium">+12%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-purple-600 h-2 rounded-full" style={{ width: "12%" }}></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Revenue Growth</span>
                  <span className="font-medium">+7.2%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-purple-600 h-2 rounded-full" style={{ width: "7.2%" }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerFlow;
